import { createSlice, Draft, PayloadAction } from '@reduxjs/toolkit';
import {
  TApiaCellDefinition,
  TApiaComplexCell,
  TApiaFilter,
} from '../../types';
import refresh from './api/actions/listActions/refresh';
import {
  IListSlice,
  TListPayload,
  TListState,
  TSetInitialState,
} from './types';
import setFilters from './api/actions/listActions/setFilters';
import sort from './api/actions/listActions/sort';
import filter from './api/actions/listActions/filter';
import page from './api/actions/listActions/page';
import deleteFilters from './api/actions/listActions/deleteFilters';
import defaultRowParser from './api/util/rowsParser';
import jspToResponsiveColumnsParser from './api/util/jspToResponsiveColumnsParser';
import AsyncRenderer from '../../components/apia/common/lists/renderers/AsyncRenderer';
import AccordionAsyncRenderer from '../../components/apia/common/lists/renderers/AccordionAsyncRenderer';
import addListActions from './api/actions/listActions';
import addTaskControlActions from './api/actions/taskControlActions';
import filterEntities from './api/actions/entitiesMonitorActions/filterEntities';
import loadFilters from './api/actions/taskControlActions/loadFilters';
import filterTasks from './api/actions/taskControlActions/filterTasks';
import loadTskMonFilters from './api/actions/taskMonitorActions/loadFilters';
import filterTskMonTasks from './api/actions/taskMonitorActions/filterTasks';
import addEntitiesMonitorActions from './api/actions/entitiesMonitorActions';
import addTaskMonitorActions from './api/actions/taskMonitorActions';

const getInitialState: (props: TSetInitialState) => TListState = (
  initialState: TSetInitialState,
) => {
  return {
    areAllFiltersApplied: true,
    currentChart: undefined,
    customLabel: window.MSG_FIRST_TIME ?? '',
    freeMetaFilters: [],
    hasAdditionalInfoInTable: false,
    hasSearched: false,
    inputValue: '',
    lastSearchAppliedFilters: 0,
    loading: 0,
    metaFilters: {
      allowFreeMeta: false,
      processInfo: '',
      docType: undefined,
      filters: [],
    },
    shouldShowModal: false,
    rows: [],
    ...initialState,
    ajaxUrl: initialState.ajaxUrl ?? window.URL_REQUEST_AJAX,
  };
};

export function isComplexCell(
  cell: TApiaCellDefinition,
): cell is TApiaComplexCell {
  return typeof cell !== 'string';
}

export const initLoad = (state: Draft<IListSlice>, listId: string) => {
  state.lists[listId].loading += 1;
};

export const finishLoad = (
  state: Draft<IListSlice>,
  listId: string,
  countFilters = true,
  filters?: TApiaFilter[],
) => {
  state.lists[listId].loading -= 1;

  if (countFilters) {
    if (filters) {
      const appliedFilters = new Set();

      filters?.forEach((incomingFilter) => {
        if (
          !(
            [
              'processesAdHocControl',
              'processesAdHocControl',
              'processFinalizeControl',
              'processPriorityControl',
              'processReasignControl',
              'processesRollbackControl',
              'processChangeDateControl',
            ].includes(window.listData[listId].component) &&
            incomingFilter.id === 'cmbSta'
          )
        ) {
          if (
            ['date', 'D'].includes(incomingFilter.type as string) ||
            incomingFilter.isRange
          ) {
            if (
              incomingFilter.currentValue !== '' &&
              incomingFilter.currentValue !== undefined
            ) {
              appliedFilters.add(incomingFilter.id);
            } else if (incomingFilter.currentValue === '') {
              appliedFilters.delete(incomingFilter.id);
            }
            if (
              incomingFilter.filterToValue !== '' &&
              incomingFilter.filterToValue !== undefined
            ) {
              appliedFilters.add(incomingFilter.filterToId);
            } else if (incomingFilter.filterToValue === '') {
              appliedFilters.delete(incomingFilter.filterToId);
            }
          } else if (
            incomingFilter.currentValue !== '' &&
            incomingFilter.currentValue !== undefined &&
            !(
              ['N', 'number', 'apiaNumber'].includes(
                incomingFilter.type ?? '',
              ) && incomingFilter.currentValue === 'null'
            )
          ) {
            appliedFilters.add(incomingFilter.id);
          } else if (incomingFilter.currentValue === '') {
            appliedFilters.delete(incomingFilter.id);
          }
        }
      });

      state.lists[listId].lastSearchAppliedFilters = appliedFilters.size;
    } else {
      const appliedFilters = new Set();

      state.lists[listId].filters?.forEach((incomingFilter) => {
        if (
          !(
            [
              'processesAdHocControl',
              'processesAdHocControl',
              'processFinalizeControl',
              'processPriorityControl',
              'processReasignControl',
              'processesRollbackControl',
              'processChangeDateControl',
            ].includes(window.listData[listId].component) &&
            incomingFilter.id === 'cmbSta'
          )
        ) {
          if (
            ['date', 'D'].includes(incomingFilter.type as string) ||
            incomingFilter.isRange
          ) {
            if (
              incomingFilter.currentValue !== '' &&
              incomingFilter.currentValue !== undefined
            ) {
              appliedFilters.add(incomingFilter.id);
            } else if (incomingFilter.currentValue === '') {
              appliedFilters.delete(incomingFilter.id);
            }
            if (
              incomingFilter.filterToValue !== '' &&
              incomingFilter.filterToValue !== undefined
            ) {
              appliedFilters.add(incomingFilter.filterToId);
            } else if (incomingFilter.filterToValue === '') {
              appliedFilters.delete(incomingFilter.filterToId);
            }
          } else if (
            incomingFilter.currentValue !== '' &&
            incomingFilter.currentValue !== undefined &&
            !(
              ['N', 'number', 'apiaNumber'].includes(
                incomingFilter.type ?? '',
              ) && incomingFilter.currentValue === 'null'
            )
          ) {
            appliedFilters.add(incomingFilter.id);
          } else if (incomingFilter.currentValue === '') {
            appliedFilters.delete(incomingFilter.id);
          }
        }
      });

      state.lists[listId].lastSearchAppliedFilters = appliedFilters.size;
    }
  }
  if (state.lists[listId].loading < 0) state.lists[listId].loading = 0;
};

const listSlice = createSlice({
  name: 'list',
  initialState: {
    lists: {},
  } as IListSlice,
  reducers: {
    initState(
      state,
      {
        payload: { listId, initialState },
      }: PayloadAction<{
        listId: string;
        initialState?: TSetInitialState;
      }>,
    ) {
      const listData = window.listData[listId];
      if (!listData && !initialState) {
        throw new Error(`No de ha podido encontrar la lista ${listId}`);
      }

      const actualInitialState: Partial<TListState> = {
        rowsParser: defaultRowParser,
        filters: listData?.filters.map((currentFilter) => {
          const newFilter = {
            ...currentFilter,
          };
          if (currentFilter.options) {
            currentFilter.options.forEach((option) => {
              if (option.selected) {
                newFilter.currentValue = option.value;
              }
            });
          }
          return newFilter;
        }),
        columns:
          (listData !== undefined
            ? jspToResponsiveColumnsParser({
                accordeonAsyncRenderer: AccordionAsyncRenderer,
                asyncRenderer: AsyncRenderer,
                columns: listData?.columns,
                listId,
              })
            : initialState?.columns) ?? [],
        hasAdditionalInfoInTable: listData?.additionalInfoInTable,
        customLabel: listData.filters.some((innerFilter) => {
          return innerFilter.required;
        })
          ? window.MSG_FILTER_REQUIRED
          : window.MSG_FIRST_TIME,
        lastSearchAppliedFilters: listData.filters.filter((innerFilter) => {
          return (
            innerFilter.currentValue !== '' &&
            innerFilter.currentValue !== undefined
          );
        }).length,
        areAllFiltersApplied: !(
          listData.filters.filter((innerFilter) => {
            return (
              innerFilter.currentValue !== '' &&
              innerFilter.currentValue !== undefined
            );
          }).length > 0
        ),
        ...initialState,
      };
      state.lists[listId] = getInitialState(actualInitialState);
    },
    setState(
      state,
      { payload: { listId, ...payload } }: TListPayload<Partial<TListState>>,
    ) {
      state.lists[listId] = {
        ...state.lists[listId],
        ...payload,
      };
    },
  },
  extraReducers: (builder) => {
    addListActions(builder);
    addTaskControlActions(builder);
    addEntitiesMonitorActions(builder);
    addTaskMonitorActions(builder);
  },
});

export const listActions = {
  ...listSlice.actions,
  refresh,
  sort,
  setFilters,
  filter,
  page,
  deleteFilters,
};

export const taskControlActions = {
  loadFilters,
  filterTasks,
};

export const taskMonitorActions = {
  loadFilters: loadTskMonFilters,
  filterTasks: filterTskMonTasks,
};

export const entitiesMonitorActions = {
  filterEntities,
};

export default listSlice.reducer;
